/**
 * Text parser utility for rendering hashtags and @mentions into clickable React elements.
 *
 * Hashtag pattern: # followed by 1-30 word characters, preceded by start-of-string or whitespace.
 * Mention pattern: @ followed by 1-30 word characters, preceded by start-of-string or whitespace.
 *
 * Usage:
 *   import { parsePostText, extractHashtags, extractMentions } from '../utils/text-parser.util';
 *   // In JSX: {parsePostText(text)}
 */

/**
 * Extract hashtags from text content.
 * Returns lowercase, unique tags without the # prefix.
 * Max 10 per post.
 * @param {string} text
 * @returns {string[]}
 */
export const extractHashtags = (text) => {
  if (!text) return [];
  const regex = /(?:^|\s)#(\w{1,30})/g;
  const tags = new Set();
  let match;
  while ((match = regex.exec(text)) !== null) {
    tags.add(match[1].toLowerCase());
  }
  return Array.from(tags).slice(0, 10);
};

/**
 * Extract @mentions from text content.
 * Returns lowercase, unique usernames without the @ prefix.
 * Max 10 per post/comment.
 * @param {string} text
 * @returns {string[]}
 */
export const extractMentions = (text) => {
  if (!text) return [];
  const regex = /(?:^|\s)@(\w{1,30})/g;
  const mentions = new Set();
  let match;
  while ((match = regex.exec(text)) !== null) {
    mentions.add(match[1].toLowerCase());
  }
  return Array.from(mentions).slice(0, 10);
};

/**
 * Parse post/comment text and return an array of React-friendly segments.
 * Each segment is either:
 *  - { type: 'text', value: string }
 *  - { type: 'hashtag', value: string, tag: string }   (tag is lowercase, no #)
 *  - { type: 'mention', value: string, username: string } (lowercase, no @)
 *
 * Components can map these segments to clickable <Link> elements.
 *
 * @param {string} text
 * @returns {Array<{ type: string, value: string, tag?: string, username?: string }>}
 */
export const parseTextSegments = (text) => {
  if (!text) return [];

  const segments = [];
  let lastIndex = 0;

  // Use a regex that doesn't require lookbehind (better browser support).
  // Matches: optional leading whitespace + (#tag | @mention)
  const regex = /(^|\s)(#(\w{1,30})|@(\w{1,30}))/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const leadingSpace = match[1]; // space before token (or empty)
    const fullMatch = match[0]; // full match including leading space
    const matchStart = match.index;

    // Push text before this match (including any gap)
    const textBefore = text.slice(lastIndex, matchStart);
    if (textBefore) {
      segments.push({ type: 'text', value: textBefore });
    }

    // Push leading whitespace as text
    if (leadingSpace) {
      segments.push({ type: 'text', value: leadingSpace });
    }

    // Determine if hashtag or mention
    if (match[3]) {
      // Hashtag: match[3] is the tag name without #
      segments.push({
        type: 'hashtag',
        value: `#${match[3]}`,
        tag: match[3].toLowerCase(),
      });
    } else if (match[4]) {
      // Mention: match[4] is the username without @
      segments.push({
        type: 'mention',
        value: `@${match[4]}`,
        username: match[4].toLowerCase(),
      });
    }

    lastIndex = matchStart + fullMatch.length;
  }

  // Push remaining text
  const remaining = text.slice(lastIndex);
  if (remaining) {
    segments.push({ type: 'text', value: remaining });
  }

  return segments;
};
